import { getListFromApiResponse } from "../Pages/utils/apiListMappers";
import { workItemToTimesheetTaskLabel } from "../Pages/TimeEntry/timeEntryMappers";
import { formatDateForInput } from "./dateUtils";
import { getResolvedUserIdsForApi } from "./userFilterOptions";

/**
 * Maps Time Entry multiselect filters to GET /work-items/my/all query fields
 * (aligned with {@link ../Redux/Thunks/projectManagementThunkUtils.js} `buildMyWorkItemsQuery`).
 */
export function buildMyWorkItemsQueryParamsFromTimeEntryFilters({
  appliedFilters = {},
  projectsData,
  clientsData,
  teamsData,
}) {
  const q = {};
  const proj = appliedFilters.project;
  if (Array.isArray(proj) && proj.length > 0 && projectsData) {
    const joined = commaSeparateIds(resolveProjectIdsFromNames(proj, projectsData));
    if (joined) q.projectId = joined;
  }
  const cli = appliedFilters.client;
  if (Array.isArray(cli) && cli.length > 0 && clientsData) {
    const joined = commaSeparateIds(resolveClientIdsFromNames(cli, clientsData));
    if (joined) q.clientId = joined;
  }
  const tm = appliedFilters.team;
  if (Array.isArray(tm) && tm.length > 0 && teamsData) {
    const joined = commaSeparateIds(resolveTeamIdsFromNames(tm, teamsData));
    if (joined) q.teamIds = joined;
  }
  return q;
}

/** GET /time-logs/my — sort field names per API */
export const TIME_LOG_SORT_FIELDS = ["log_date", "minutes_logged", "created_at"];

export const DEFAULT_TIME_LOG_FETCH = {
  page: 1,
  limit: 50,
  sortBy: "log_date",
  sortOrder: "DESC",
};

/** My Timesheet list default — omit sort; API returns oldest first (GET /time-logs/my). */
export const MY_TIME_LOG_LIST_SORT = {
  sortBy: null,
  sortOrder: null,
};

/** Local calendar day [00:00, 23:59:59.999] */
function getDayRange(date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Date window for GET /time-logs/my — `timesheetDateRange` is applied filter draft range (YYYY-MM-DD strings).
 */
export function buildMyTimeLogsDateParams(currentDate, timesheetDateRange) {
  if (timesheetDateRange?.startDate && timesheetDateRange?.endDate) {
    return {
      dateFrom: String(timesheetDateRange.startDate).slice(0, 10),
      dateTo: String(timesheetDateRange.endDate).slice(0, 10),
    };
  }
  const { start } = getDayRange(currentDate);
  const day = formatDateForInput(start);
  return { dateFrom: day, dateTo: day };
}

function resolveProjectIdFromName(projectNameOrId, projectsData) {
  if (projectNameOrId == null || String(projectNameOrId).trim() === "" || !projectsData) {
    return undefined;
  }
  const key = String(projectNameOrId).trim();
  const list = getListFromApiResponse(projectsData);
  const byId = list.find((p) => String(p.id ?? p.projectId ?? "").trim() === key);
  if (byId) {
    const id = byId.id ?? byId.projectId;
    return id != null && id !== "" ? String(id) : undefined;
  }
  const match = list.find((p) => (p.projectName ?? p.name ?? "") === key);
  const id = match?.id ?? match?.projectId;
  return id != null && id !== "" ? String(id) : undefined;
}

/** Multiple project names or IDs → UUID strings (unknown values skipped). */
export function resolveProjectIdsFromNames(names, projectsData) {
  if (!Array.isArray(names) || names.length === 0 || !projectsData) return [];
  const out = [];
  for (const name of names) {
    const id = resolveProjectIdFromName(name, projectsData);
    if (id) out.push(id);
  }
  return out;
}

/** Multiple client names → UUID strings. */
export function resolveClientIdsFromNames(names, clientsData) {
  if (!Array.isArray(names) || names.length === 0 || !clientsData) return [];
  const out = [];
  for (const name of names) {
    const id = resolveClientIdFromName(name, clientsData);
    if (id) out.push(id);
  }
  return out;
}

/** Multiple team names → UUID strings. */
export function resolveTeamIdsFromNames(names, teamsData) {
  if (!Array.isArray(names) || names.length === 0 || !teamsData) return [];
  const out = [];
  for (const name of names) {
    const id = resolveTeamIdFromName(name, teamsData);
    if (id) out.push(id);
  }
  return out;
}

/**
 * Comma-separated UUIDs for query params (`clientId=id1,id2`).
 * Backend may also accept repeated `clientId=id1&clientId=id2` — axios sends this shape as comma-separated when one string is used.
 */
export function commaSeparateIds(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return undefined;
  const parts = ids.map((x) => (x == null ? "" : String(x).trim())).filter(Boolean);
  return parts.length ? parts.join(",") : undefined;
}

/**
 * Optional `clientId`, `teamId`, `projectId`, `userId` for GET /time-logs/team-summary and /time-logs/project-summary.
 * Multiselect → comma-separated UUIDs.
 */
export function buildTimeSummaryIdQueryParams({
  appliedFilters = {},
  clientsData,
  teamsData,
  projectsData,
}) {
  const projectIds = resolveProjectIdsFromNames(appliedFilters.project, projectsData);
  const clientIds = resolveClientIdsFromNames(appliedFilters.client, clientsData);
  const teamIds = resolveTeamIdsFromNames(appliedFilters.team, teamsData);
  const userIds = getResolvedUserIdsForApi(appliedFilters.user);

  const projectId = commaSeparateIds(projectIds);
  const clientId = commaSeparateIds(clientIds);
  const teamId = commaSeparateIds(teamIds);
  const userId = commaSeparateIds(userIds);

  return {
    ...(clientId ? { clientId } : {}),
    ...(teamId ? { teamId } : {}),
    ...(projectId ? { projectId } : {}),
    ...(userId ? { userId } : {}),
  };
}

/** GET /time-logs/team-summary | /time-logs/project-summary — `clientId` filters by `projects.client_id` */
export function resolveClientIdFromName(clientName, clientsData) {
  if (clientName == null || String(clientName).trim() === "" || !clientsData) return undefined;
  const list = getListFromApiResponse(clientsData);
  const match = list.find((c) => (c.clientName ?? c.name ?? "") === clientName);
  const id = match?.id ?? match?.clientId;
  return id != null && id !== "" ? String(id) : undefined;
}

/** GET /time-logs/team-summary | /time-logs/project-summary — `teamId` */
export function resolveTeamIdFromName(teamName, teamsData) {
  if (teamName == null || String(teamName).trim() === "" || !teamsData) return undefined;
  const list = getListFromApiResponse(teamsData);
  const match = list.find((t) => (t.teamName ?? t.name ?? "") === teamName);
  const id = match?.id ?? match?.teamId;
  return id != null && id !== "" ? String(id) : undefined;
}

/**
 * When exactly one task label is selected, resolve workItemId from current rows, else from
 * GET /work-items/my/all picker list (same labels as `workItemToTimesheetTaskLabel`).
 */
export function resolveWorkItemIdFromTaskSelection(selectedTasks, listRows, myWorkItems) {
  if (!Array.isArray(selectedTasks) || selectedTasks.length !== 1) {
    return undefined;
  }
  const label = selectedTasks[0];
  if (Array.isArray(listRows)) {
    const row = listRows.find(
      (e) => e.task === label && e.workItemId != null && e.workItemId !== ""
    );
    if (row?.workItemId != null) return String(row.workItemId);
  }
  if (Array.isArray(myWorkItems)) {
    for (const item of myWorkItems) {
      if (!item || typeof item !== "object") continue;
      if (workItemToTimesheetTaskLabel(item) !== label) continue;
      const id = item.id;
      if (id != null && String(id).trim() !== "") return String(id);
    }
  }
  return undefined;
}

/** Multiple selected task labels -> comma-separated workItem ids (unknown labels skipped). */
export function resolveWorkItemIdsFromTaskSelection(selectedTasks, listRows, myWorkItems) {
  if (!Array.isArray(selectedTasks) || selectedTasks.length === 0) return [];
  const out = [];
  for (const label of selectedTasks) {
    const id = resolveWorkItemIdFromTaskSelection([label], listRows, myWorkItems);
    if (id) out.push(id);
  }
  return out;
}

/**
 * Full query params for GET /time-logs/my (filter + pagination + sort).
 *
 * @param {object} options
 * @param {Date} options.currentDate
 * @param {object|null} options.timesheetDateRange — applied range `{ startDate, endDate }`
 * @param {object} options.appliedFilters — `{ client, project, task, team, user }` multiselects
 * @param {object|null} options.projectsData — Redux projects list payload
 * @param {object|null} [options.clientsData] — Redux clients list (`clientId`: comma-separated UUIDs)
 * @param {object|null} [options.teamsData] — Redux teams list (`teamId`: comma-separated UUIDs)
 * @param {string|null|undefined} options.appliedWorkItemId — set on “Filter” when one task is selected and id was resolved from current rows (optional UUID)
 * @param {number} [options.page]
 * @param {number} [options.limit]
 * @param {string} [options.sortBy]
 * @param {string} [options.sortOrder]
 */
export function buildMyTimeLogsFetchParams({
  currentDate,
  timesheetDateRange,
  appliedFilters = {},
  projectsData,
  clientsData,
  teamsData,
  appliedWorkItemId,
  page = DEFAULT_TIME_LOG_FETCH.page,
  limit = DEFAULT_TIME_LOG_FETCH.limit,
  sortBy = DEFAULT_TIME_LOG_FETCH.sortBy,
  sortOrder = DEFAULT_TIME_LOG_FETCH.sortOrder,
  /** My Timesheet tab does not expose team/user filters — omit from query when false */
  includeTeamUserFilters = true,
}) {
  const dates = buildMyTimeLogsDateParams(currentDate, timesheetDateRange);

  const safePage = Math.max(1, Number(page) || 1);
  const safeLimit = Math.min(500, Math.max(1, Number(limit) || DEFAULT_TIME_LOG_FETCH.limit));
  const safeSortOrder =
    sortOrder === "ASC" || sortOrder === "DESC" ? sortOrder : DEFAULT_TIME_LOG_FETCH.sortOrder;
  const hasSortBy = sortBy != null && sortBy !== "" && TIME_LOG_SORT_FIELDS.includes(sortBy);

  const params = {
    ...dates,
    page: safePage,
    limit: safeLimit,
  };

  if (sortOrder != null && sortOrder !== "") {
    params.sortOrder = safeSortOrder;
    if (hasSortBy) params.sortBy = sortBy;
  } else if (hasSortBy) {
    params.sortBy = sortBy;
    params.sortOrder = DEFAULT_TIME_LOG_FETCH.sortOrder;
  }

  const proj = appliedFilters.project;
  if (Array.isArray(proj) && proj.length > 0 && projectsData) {
    const joined = commaSeparateIds(resolveProjectIdsFromNames(proj, projectsData));
    if (joined) params.projectId = joined;
  }

  if (appliedWorkItemId != null && String(appliedWorkItemId).trim() !== "") {
    params.workItemId = String(appliedWorkItemId).trim();
  }

  const cli = appliedFilters.client;
  if (Array.isArray(cli) && cli.length > 0 && clientsData) {
    const joined = commaSeparateIds(resolveClientIdsFromNames(cli, clientsData));
    if (joined) params.clientId = joined;
  }

  if (includeTeamUserFilters) {
    const tm = appliedFilters.team;
    if (Array.isArray(tm) && tm.length > 0 && teamsData) {
      const joined = commaSeparateIds(resolveTeamIdsFromNames(tm, teamsData));
      if (joined) params.teamId = joined;
    }

    const usr = appliedFilters.user;
    if (Array.isArray(usr) && usr.length > 0) {
      const joined = commaSeparateIds(getResolvedUserIdsForApi(usr));
      if (joined) params.userId = joined;
    }
  }

  return params;
}

/**
 * GET /time-logs — same shape as {@link buildMyTimeLogsFetchParams} with team/user filters enabled.
 */
/**
 * GET /time-logs — when `newestFirst` is false, omit sort (API default, oldest first).
 * When true: `sortBy=log_date`, `sortOrder=DESC`.
 */
export function buildAllTimeLogsFetchParams(options = {}) {
  const { newestFirst = false, ...rest } = options;
  return buildMyTimeLogsFetchParams({
    ...rest,
    includeTeamUserFilters: true,
    appliedWorkItemId: null,
    sortBy: newestFirst ? "log_date" : null,
    sortOrder: newestFirst ? "DESC" : null,
  });
}

/**
 * Query params for GET /time-logs/export — All Time Entries tab.
 * Same filters as list (client, project, team, user, date range); `sortBy=log_date`, `sortOrder=DESC`.
 */
export function buildAllTimeLogsExportQueryParams({
  currentDate,
  timesheetDateRange,
  appliedFilters = {},
  projectsData,
  clientsData,
  teamsData,
  newestFirst = false,
}) {
  const p = buildMyTimesheetExportQueryParams({
    currentDate,
    timesheetDateRange,
    appliedFilters: {
      client: appliedFilters.client ?? [],
      project: appliedFilters.project ?? [],
      team: appliedFilters.team ?? [],
      user: appliedFilters.user ?? [],
    },
    projectsData,
    clientsData,
    teamsData,
    listRows: null,
    myWorkItems: null,
    appliedWorkItemId: null,
  });
  delete p.workItemId;
  if (newestFirst) {
    p.sortBy = "log_date";
    p.sortOrder = "DESC";
  } else {
    delete p.sortBy;
    delete p.sortOrder;
  }
  return p;
}

/**
 * Query params for GET /time-logs/my/export — mirrors list filters where applicable.
 */
export function buildMyTimesheetExportQueryParams({
  currentDate,
  timesheetDateRange,
  appliedFilters = {},
  projectsData,
  clientsData,
  teamsData,
  appliedWorkItemId,
  listRows,
  myWorkItems,
}) {
  const { dateFrom, dateTo } = buildMyTimeLogsDateParams(currentDate, timesheetDateRange);
  const params = { dateFrom, dateTo };
  const proj = appliedFilters.project;
  if (Array.isArray(proj) && proj.length > 0 && projectsData) {
    const joined = commaSeparateIds(resolveProjectIdsFromNames(proj, projectsData));
    if (joined) params.projectId = joined;
  }
  const cli = appliedFilters.client;
  if (Array.isArray(cli) && cli.length > 0 && clientsData) {
    const joined = commaSeparateIds(resolveClientIdsFromNames(cli, clientsData));
    if (joined) params.clientId = joined;
  }
  const tm = appliedFilters.team;
  if (Array.isArray(tm) && tm.length > 0 && teamsData) {
    const joined = commaSeparateIds(resolveTeamIdsFromNames(tm, teamsData));
    if (joined) params.teamId = joined;
  }
  const usr = appliedFilters.user;
  if (Array.isArray(usr) && usr.length > 0) {
    const joined = commaSeparateIds(getResolvedUserIdsForApi(usr));
    if (joined) params.userId = joined;
  }
  const selectedTasks = appliedFilters.task;
  if (Array.isArray(selectedTasks) && selectedTasks.length > 0) {
    const joined = commaSeparateIds(
      resolveWorkItemIdsFromTaskSelection(selectedTasks, listRows, myWorkItems)
    );
    if (joined) params.workItemId = joined;
  }
  if (!params.workItemId && appliedWorkItemId != null && String(appliedWorkItemId).trim() !== "") {
    params.workItemId = String(appliedWorkItemId).trim();
  }
  return params;
}

/**
 * Query params for GET /time-logs/team-summary/export (CSV).
 * Merges existing team-summary list params with export defaults.
 */
export function buildTeamSummaryExportQueryParams(teamSummaryQueryParams = {}) {
  const { dateFrom, dateTo, ...rest } = teamSummaryQueryParams;
  return {
    ...rest,
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    includeDetails: true,
    sortBy: "total_minutes",
    sortOrder: "DESC",
  };
}

/**
 * Query params for GET /time-logs/project-summary/export.
 * Uses same filters/date as project summary list with project export sort defaults.
 */
export function buildProjectSummaryExportQueryParams(projectSummaryQueryParams = {}) {
  const { dateFrom, dateTo, ...rest } = projectSummaryQueryParams;
  return {
    ...rest,
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    includeDetails: true,
    sortBy: "project_name",
    sortOrder: "ASC",
  };
}

/**
 * Query params for GET /time-logs/developer-summary/export (CSV).
 * Merges existing developer-summary list params with export defaults.
 */
export function buildDeveloperSummaryExportQueryParams(developerSummaryQueryParams = {}) {
  const { dateFrom, dateTo, ...rest } = developerSummaryQueryParams;
  return {
    ...rest,
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    sortBy: "logDate",
    sortOrder: "ASC",
  };
}
